import { useMemo, useState } from 'react';
import { useDndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { isSchoolEvent } from '@features/chessrussian/customizations/utils/event-filters';
import { MemberAux } from '@features/club/typings';
import { ParticipantDto, RcfAttributes, Tournament } from '@features/shared/api/typings';
import { ParticipantRow } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/ParticipantRow';
import {
  NO_TEAM,
  useGroupedParticipantsByTeams,
} from '@features/tournament/ui/components/TournamentParticipantsEdit/components/participantsHelpers';
import { TeamOptionType } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/SelectTeamPopover';
import { TeamWithParticipants } from '@features/tournament/ui/components/TournamentParticipantsEdit/components/TeamWithParticipants';

type TableBodyProps = {
  tournament: Tournament;
  participants?: ParticipantDto[];
  showTeams: boolean;
  showNumbers: boolean;
  isRcf: boolean;
  onRcfAttributesChange?: (newValue: RcfAttributes) => void;
  onCamerasChange?: (userId: string, aux: MemberAux | null) => void;
  onBanTeam?: (teamId: string) => void;
  onRenameTeam?: (teamId: string, name: string) => void;
  onDeleteTeam?: (teamId: string) => void;
  onDelete?: (id: string) => void;
  onAccept?: (id: string) => void;
  onRestore?: (id: string) => void;
  onRemove?: (id: string) => void;
  onBan?: (id: string) => void;
  onPgnDownload?: (id: string) => void;
  onSelectTeam?: (userId: string, teamId: string | null) => void;
  onSetOrder?: (id: string, num: number) => void;
  sortDisabled?: boolean;
};
export function TableBody({
  tournament,
  participants,
  showTeams,
  showNumbers,
  isRcf,
  onBanTeam,
  onRenameTeam,
  onDeleteTeam,
  onDelete,
  onAccept,
  onRestore,
  onRemove,
  onBan,
  onPgnDownload,
  onSelectTeam,
  onRcfAttributesChange,
  onCamerasChange,
  onSetOrder,
  sortDisabled,
}: TableBodyProps) {
  const { active } = useDndContext();

  const [openRcfModalUserId, setOpenRcfModalUserId] = useState<string | null>(null);

  const groupedParticipants = useGroupedParticipantsByTeams(participants);
  const teamIds = tournament.teams?.map((t) => t.teamId) || [];

  const teamlessParticipants = showTeams ? groupedParticipants[NO_TEAM] : participants;

  const teamOptions: TeamOptionType[] = useMemo(() => {
    return (
      tournament.teams?.map((t) => ({
        teamId: t.teamId,
        name: t.name,
        disabled: Boolean(
          groupedParticipants[t.teamId] &&
          tournament.teamModeSettings?.size &&
          tournament.teamModeSettings.size <= groupedParticipants[t.teamId]!.length,
        ),
      })) || []
    );
  }, [tournament.teamModeSettings, tournament.teams, groupedParticipants]);

  return (
    <SortableContext items={teamIds} strategy={verticalListSortingStrategy}>
      <SortableContext items={teamlessParticipants?.map((p) => p.userId) || []}>
        {teamlessParticipants?.map((p) => (
          <ParticipantRow
            key={p.userId}
            participant={p}
            num={p.numberInTournament!}
            teamOptions={teamOptions}
            isSchoolEvent={isSchoolEvent(tournament)}
            isRcf={isRcf}
            isRcfModalOpen={openRcfModalUserId === p.userId}
            setRcfModalOpen={setOpenRcfModalUserId}
            onRcfAttributesChange={onRcfAttributesChange}
            onCamerasChange={onCamerasChange}
            disabled={active?.data.current?.type === 'team'}
            sortDisabled={sortDisabled}
            showNumbers={showNumbers}
            onDelete={onDelete}
            onAccept={onAccept}
            onRestore={onRestore}
            onRemove={onRemove}
            onBan={onBan}
            onPgnDownload={onPgnDownload}
            onSelectTeam={onSelectTeam}
          />
        ))}
      </SortableContext>

      {showTeams &&
        tournament.teams?.map((team, i) => (
          <TeamWithParticipants
            key={team.teamId}
            team={team}
            num={i + 1}
            participants={groupedParticipants[team.teamId] || []}
            teamOptions={teamOptions}
            isSchoolEvent={isSchoolEvent(tournament)}
            isRcf={isRcf}
            openRcfModalUserId={openRcfModalUserId}
            setOpenRcfModalUserId={setOpenRcfModalUserId}
            sortDisabled={sortDisabled}
            showNumbers={showNumbers}
            maxParticipantsInTeam={tournament.teamModeSettings!.size}
            onBanTeam={onBanTeam}
            onRenameTeam={onRenameTeam}
            onDeleteTeam={onDeleteTeam}
            onDelete={onDelete}
            onAccept={onAccept}
            onRemove={onRemove}
            onBan={onBan}
            onPgnDownload={onPgnDownload}
            onSelectTeam={onSelectTeam}
            onRcfAttributesChange={onRcfAttributesChange}
            onCamerasChange={onCamerasChange}
            onSetOrder={onSetOrder}
          />
        ))}
    </SortableContext>
  );
}
